import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Edit, Trash2, KeyRound, Download, Mail, Phone, CheckCircle2,
  XCircle, Loader2, ShieldCheck, ArrowUp, ArrowDown, FileSpreadsheet, FileText,
} from "lucide-react";
import * as XLSX from "xlsx";

import { exportToPDF } from "../../lib/export";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";

import { API_URL } from "../../config";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const providerLabel = {
  password: "Email + Password",
  google: "Google",
  both: "Email + Google",
};

export const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", phone: "", email: "", email_verified: false });
  const [saving, setSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resetingUser, setResetingUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data || []);
    } catch (err) {
      toast.error("Erro ao carregar utilizadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
        if (!cancelled) setUsers(data || []);
      } catch (err) {
        if (!cancelled) toast.error("Erro ao carregar utilizadores");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter + sort applied inline (no useMemo to satisfy lint rule that
  // misfires on hook-like helpers; users list is small enough that
  // recomputation per render is negligible).
  const _searchLower = (search || "").trim().toLowerCase();
  const _baseList = _searchLower
    ? users.filter((u) =>
        (u.email || "").toLowerCase().includes(_searchLower) ||
        (u.first_name || "").toLowerCase().includes(_searchLower) ||
        (u.last_name || "").toLowerCase().includes(_searchLower) ||
        (u.phone || "").toLowerCase().includes(_searchLower)
      )
    : users;
  const _sortMult = sort.dir === "asc" ? 1 : -1;
  const filtered = [..._baseList].sort((a, b) => {
    const av = a[sort.key] ?? "";
    const bv = b[sort.key] ?? "";
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * _sortMult;
    return String(av).localeCompare(String(bv)) * _sortMult;
  });

  const toggleSort = (key) => setSort((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const renderSortIcon = (k) => sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="inline w-3 h-3 ml-1" /> : <ArrowDown className="inline w-3 h-3 ml-1" />) : null;

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      email: user.email || "",
      email_verified: !!user.email_verified,
    });
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${editingUser.id}`, editForm, { withCredentials: true });
      toast.success("Utilizador actualizado");
      setEditingUser(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const doResetPassword = async () => {
    if (!resetingUser) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${resetingUser.id}/reset-password`,
        {},
        { withCredentials: true }
      );
      toast[data.sent ? "success" : "warning"](data.message);
      setResetingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar reset");
    }
  };

  const doDelete = async () => {
    if (!deletingUser) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${deletingUser.id}`, { withCredentials: true });
      toast.success("Utilizador removido");
      setDeletingUser(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover");
    }
  };

  const exportXlsx = () => {
    const rows = filtered.map((u) => ({
      "Email": u.email,
      "Nome": `${u.first_name || ""} ${u.last_name || ""}`.trim(),
      "Telefone": u.phone || "",
      "Provedor": providerLabel[u.auth_provider] || u.auth_provider,
      "Email Verificado": u.email_verified ? "Sim" : "Não",
      "Bilhetes": u.ticket_count || 0,
      "Total Gasto (€)": (u.total_spent || 0).toFixed(2),
      "Criado": fmtDateTime(u.created_at),
      "Último Login": fmtDateTime(u.last_login_at),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Utilizadores");
    XLSX.writeFile(wb, `utilizadores_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = () => {
    const rows = filtered.map((u) => ({
      email: u.email,
      nome: `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—",
      telefone: u.phone || "—",
      provedor: providerLabel[u.auth_provider] || u.auth_provider,
      verified: u.email_verified ? "Sim" : "Não",
      bilhetes: u.ticket_count || 0,
      total: `${(u.total_spent || 0).toFixed(2)}€`,
      ultimo_login: fmtDateTime(u.last_login_at),
      criado: fmtDate(u.created_at),
    }));
    const columns = [
      { header: "Email", key: "email" },
      { header: "Nome", key: "nome" },
      { header: "Telefone", key: "telefone" },
      { header: "Provedor", key: "provedor" },
      { header: "Verif.", key: "verified" },
      { header: "Bilhetes", key: "bilhetes" },
      { header: "Total", key: "total" },
      { header: "Último Login", key: "ultimo_login" },
      { header: "Criado", key: "criado" },
    ];
    const title = `Utilizadores Registados (${filtered.length})`;
    const filename = `utilizadores_${new Date().toISOString().slice(0, 10)}`;
    exportToPDF(rows, columns, title, filename);
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Utilizadores</h1>
          <p className="text-zinc-500 text-sm mt-1">Gerir as Contas de Utilizadores registados</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por nome/email/telefone..."
              className="input-dark pl-10 w-72"
              data-testid="users-search"
            />
          </div>
          <Button onClick={exportXlsx} variant="outline" className="border-white/10 text-zinc-200" data-testid="users-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={exportPdf} variant="outline" className="border-white/10 text-zinc-200" data-testid="users-export-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Total Users Stat Card */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-5" data-testid="users-total-card">
          <p className="text-zinc-400 text-xs uppercase tracking-wider font-semibold">Total de Utilizadores</p>
          <p className="text-3xl font-bold text-white mt-2">{users.length}</p>
          <p className="text-zinc-500 text-xs mt-1">{filtered.length} visíveis após filtros</p>
        </div>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm" data-testid="users-empty">
            {search ? "Nenhum utilizador corresponde à pesquisa." : "Ainda não há utilizadores registados."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-black/40 text-zinc-400 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort("email")}>Email {renderSortIcon("email")}</th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort("first_name")}>Nome {renderSortIcon("first_name")}</th>
                  <th className="px-4 py-3 text-left">Telefone</th>
                  <th className="px-4 py-3 text-left">Provedor</th>
                  <th className="px-4 py-3 text-center cursor-pointer hover:text-white" onClick={() => toggleSort("ticket_count")}>Bilhetes {renderSortIcon("ticket_count")}</th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:text-white" onClick={() => toggleSort("total_spent")}>Total {renderSortIcon("total_spent")}</th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort("last_login_at")}>Último Login {renderSortIcon("last_login_at")}</th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:text-white" onClick={() => toggleSort("created_at")}>Criado {renderSortIcon("created_at")}</th>
                  <th className="px-4 py-3 text-center">Acções</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 text-zinc-100">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-zinc-500" />
                        <span>{u.email}</span>
                        {u.email_verified ? (
                          <span title="Email verificado"><ShieldCheck className="w-4 h-4 text-emerald-400" /></span>
                        ) : (
                          <span title="Email não verificado" className="text-zinc-500"><XCircle className="w-3.5 h-3.5" /></span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-200">{`${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">{u.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-zinc-500" />{u.phone}</span> : "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{providerLabel[u.auth_provider] || u.auth_provider}</td>
                    <td className="px-4 py-3 text-center text-zinc-200 font-medium">{u.ticket_count || 0}</td>
                    <td className="px-4 py-3 text-right text-amber-300 font-medium">{(u.total_spent || 0).toFixed(2)}€</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDateTime(u.last_login_at)}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors" title="Editar" data-testid={`user-edit-${u.id}`}><Edit className="w-4 h-4" /></button>
                        <button onClick={() => setResetingUser(u)} className="p-1.5 text-sky-500 hover:text-sky-400 hover:bg-sky-500/10 rounded transition-colors" title="Enviar reset de password" data-testid={`user-reset-${u.id}`}><KeyRound className="w-4 h-4" /></button>
                        <button onClick={() => setDeletingUser(u)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors" title="Remover" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─────────── Edit dialog ─────────── */}
      <Dialog open={!!editingUser} onOpenChange={(v) => !v && setEditingUser(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Utilizador</DialogTitle>
            <DialogDescription className="text-zinc-400">Alterar dados do utilizador. O email pode ser alterado se não estiver em uso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Primeiro nome</Label>
                <Input value={editForm.first_name} onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))} className="input-dark mt-1.5" data-testid="user-edit-first-name" />
              </div>
              <div>
                <Label className="text-zinc-300">Último nome</Label>
                <Input value={editForm.last_name} onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))} className="input-dark mt-1.5" data-testid="user-edit-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} className="input-dark mt-1.5" data-testid="user-edit-email" />
            </div>
            <div>
              <Label className="text-zinc-300">Telefone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))} className="input-dark mt-1.5" data-testid="user-edit-phone" />
            </div>
            <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-2.5">
              <div>
                <p className="text-sm text-white font-medium">Email verificado</p>
                <p className="text-xs text-zinc-500">Marcar como verificado manualmente</p>
              </div>
              <Switch checked={editForm.email_verified} onCheckedChange={(v) => setEditForm((p) => ({ ...p, email_verified: v }))} data-testid="user-edit-verified" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-white/10 text-zinc-300" onClick={() => setEditingUser(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} className="btn-primary" data-testid="user-edit-save">{saving ? "A guardar..." : "Guardar"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─────────── Reset confirm ─────────── */}
      <AlertDialog open={!!resetingUser} onOpenChange={(v) => !v && setResetingUser(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10 text-white" data-testid="user-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Enviar reset de password?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Será enviado um email para <strong>{resetingUser?.email}</strong> com um link válido por 24 horas para definir uma nova password. A password actual não é alterada até o utilizador concluir o reset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doResetPassword} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="user-reset-confirm">Enviar email</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─────────── Delete confirm ─────────── */}
      <AlertDialog open={!!deletingUser} onOpenChange={(v) => !v && setDeletingUser(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10 text-white" data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remover utilizador?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              A conta de <strong>{deletingUser?.email}</strong> será permanentemente removida. Os bilhetes comprados <strong>mantêm-se no sistema</strong> mas deixarão de estar associados a esta conta (continuam acessíveis pela URL/email do atendente).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="user-delete-confirm">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
